import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newProject, setNewProject] = useState({
    project_number: '',
    project_name: '',
    description: '',
    location: '',
    start_date: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('خطأ في جلب البيانات');
    }
  };

  const handleAdd = async () => {
    if (!newProject.project_number.trim() || !newProject.project_name.trim()) {
      toast.error('الرجاء إدخال رقم المشروع واسم المشروع');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/projects`, newProject);
      toast.success('تم إضافة المشروع بنجاح');
      setNewProject({
        project_number: '',
        project_name: '',
        description: '',
        location: '',
        start_date: ''
      });
      fetchProjects();
    } catch (error) {
      toast.error('خطأ في إضافة المشروع');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success('تم حذف المشروع بنجاح');
      fetchProjects();
    } catch (error) {
      toast.error('خطأ في حذف المشروع');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-slate-100 p-6">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Manrope'}}>
              إدارة المشاريع
            </h1>
            <p className="text-slate-600">أضف وأدر بيانات المشاريع</p>
          </div>
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/procurement')} 
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="w-5 h-5 shrink-0" />
            <span>العودة</span>
          </Button>
        </div>

        <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>إضافة مشروع جديد</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project-number">رقم المشروع</Label>
                <Input
                  id="project-number"
                  data-testid="project-number-input"
                  placeholder="مثال: P-2025-001"
                  value={newProject.project_number}
                  onChange={(e) => setNewProject({...newProject, project_number: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="project-name">اسم المشروع</Label>
                <Input
                  id="project-name"
                  data-testid="project-name-input"
                  placeholder="اسم المشروع"
                  value={newProject.project_name}
                  onChange={(e) => setNewProject({...newProject, project_name: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="location">الموقع</Label>
                <Input
                  id="location"
                  data-testid="location-input"
                  placeholder="موقع المشروع"
                  value={newProject.location}
                  onChange={(e) => setNewProject({...newProject, location: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="start-date">تاريخ البداية</Label>
                <Input
                  id="start-date"
                  data-testid="start-date-input"
                  type="date"
                  value={newProject.start_date}
                  onChange={(e) => setNewProject({...newProject, start_date: e.target.value})}
                  className="mt-2"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="description">الوصف</Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  placeholder="وصف المشروع"
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>
            <Button 
              data-testid="add-project-btn"
              onClick={handleAdd} 
              disabled={loading}
              className="w-full mt-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:shadow-lg"
            >
              <Plus className="w-4 h-4 ml-2" />
              إضافة المشروع
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>قائمة المشاريع</CardTitle>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <p className="text-center text-slate-500 py-8">لا توجد مشاريع. قم بإضافة مشروع جديد</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-right p-3 font-semibold text-slate-700">رقم المشروع</th>
                      <th className="text-right p-3 font-semibold text-slate-700">اسم المشروع</th>
                      <th className="text-right p-3 font-semibold text-slate-700">الموقع</th>
                      <th className="text-right p-3 font-semibold text-slate-700">تاريخ البداية</th>
                      <th className="text-right p-3 font-semibold text-slate-700">الوصف</th>
                      <th className="text-right p-3 font-semibold text-slate-700">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id} data-testid={`project-row-${project.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3 text-slate-800 font-semibold">{project.project_number}</td>
                        <td className="p-3 text-slate-800 font-medium">{project.project_name}</td>
                        <td className="p-3 text-slate-600">{project.location || '-'}</td>
                        <td className="p-3 text-slate-600">{project.start_date || '-'}</td>
                        <td className="p-3 text-slate-600 text-sm">{project.description || '-'}</td>
                        <td className="p-3">
                          <Button
                            data-testid={`delete-project-${project.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(project.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
